import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AiUtility } from 'countable@helpers';
import { ChangeEngModel, LucaEngListModel, LucaSortModel } from 'countable@model';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-ai-engagements',
  templateUrl: './ai-engagements.component.html',
  styleUrl: './ai-engagements.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AiEngagementsComponent {

  @ViewChild('sessionManagement') sessionManagement: TemplateRef<any>;

  @Input() set engList(list: LucaEngListModel[]) {
    this.engagementsList.set(structuredClone(list));
    this.originalEngList.set(structuredClone(list));
  }

  @Input() set searchText(text: string) {
    this.search.next(text);
  }

  @Input() set selectedEng(eng: LucaEngListModel) {
    this.engInfo.set(structuredClone(eng));
  }

  @Output() changeEngagement: EventEmitter<ChangeEngModel> = new EventEmitter<ChangeEngModel>(true);
  public engagementsList: WritableSignal<LucaEngListModel[]> = signal<LucaEngListModel[]>([]);
  public originalEngList: WritableSignal<LucaEngListModel[]> = signal<LucaEngListModel[]>([]);
  public search: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public dateSort: WritableSignal<LucaSortModel> = signal<LucaSortModel>(null);
  public engInfo: WritableSignal<LucaEngListModel> = signal<LucaEngListModel>(null);
  public yearSort: WritableSignal<LucaSortModel> = signal<LucaSortModel>(null);
  public aiUtil: typeof AiUtility = AiUtility;

  constructor(private dialog: MatDialog) {

    this.dateSort.set(this.aiUtil?.getSortType('DATE', 'DEF'));
    this.yearSort.set(this.aiUtil?.getSortType('YEAR', 'DEF'));

    this.search.pipe(
      distinctUntilChanged((previous, current) => {
        return previous === current;
      })
    ).subscribe({
      next: res => {
        if (!!res) {
          this.searchInEngList(res);
        } else {
          this.engagementsList.set(this.originalEngList());
        }
      }
    });
  }

  sortList(type: 'CREATED_DATE' | 'YEAR_END') {
    if (type === 'CREATED_DATE') {
      const sortActionType = this.aiUtil.getSortActionType(this.dateSort()?.sortType);
      this.dateSort.set(this.aiUtil.getSortType('DATE', sortActionType));
      if (sortActionType === 'ASC' || sortActionType === 'DSC') {
        const sortedData = this.aiUtil.sortByEngCreatedDate(structuredClone(this.engagementsList()), sortActionType);
        this.engagementsList.set(sortedData);
      } else {
        this.engagementsList.set(structuredClone(this.originalEngList()));
      }
    }
    if (type === 'YEAR_END') {
      const sortActionType = this.aiUtil.getSortActionType(this.yearSort()?.sortType);
      this.yearSort.set(this.aiUtil.getSortType('YEAR', sortActionType));
      if (sortActionType === 'ASC' || sortActionType === 'DSC') {
        const sortedData = this.aiUtil.sortByYearEnd(structuredClone(this.engagementsList()), sortActionType);
        this.engagementsList.set(sortedData);
      } else {
        this.engagementsList.set(structuredClone(this.originalEngList()));
      }
    }
  }

  setSelectedEngagement(event, eng: LucaEngListModel) {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.engInfo() && this.engInfo()?.engId !== eng?.engId) {
      this.sessionManagementDialog(eng);
    } else if (this.engInfo() && this.engInfo()?.engId === eng?.engId) {
      this.changeEngagement.emit({
        engId: null,
        type: 'CONTINUE',
        searchText: eng?.engName,
        isEngRestricted: eng?.isRestricted
      });
    } else {
      this.engInfo.set(structuredClone(eng));
      this.changeEngagement.emit({
        engId: eng?.engId,
        type: 'NORMAL',
        searchText: eng?.engName,
        isEngRestricted: eng?.isRestricted
      });
    }
  }

  private sessionManagementDialog(eng: LucaEngListModel) {
    this.dialog.open(this.sessionManagement, {
      disableClose: true
    }).afterClosed().subscribe({
      next: res => {
        if (res) {
          this.engInfo.set(structuredClone(eng));
          this.changeEngagement.emit({
            engId: eng?.engId,
            type: 'NEW',
            searchText: eng?.engName,
            isEngRestricted: eng?.isRestricted
          });
        } else {
          this.changeEngagement.emit({
            engId: null,
            type: 'CONTINUE',
            searchText: this.engInfo()?.engName,
            isEngRestricted: this.engInfo()?.isRestricted
          });
        }
      }
    });
  }

  public searchInEngList(text: string = '') {
    if (!text) {
      return;
    }
    const engList = structuredClone(this.originalEngList());
    const filterList = engList?.filter(e => e?.engName?.toLocaleLowerCase()?.includes(text?.toLocaleLowerCase())
      || e?.clientName?.toLocaleLowerCase()?.includes(text?.toLocaleLowerCase()));
    this.engagementsList.set(filterList);
  }

}
